import { parse, format, addDays, addMonths, addMinutes, startOfDay, endOfDay, differenceInDays, isWithinRange } from 'date-fns'
import { loadScript } from '@/libs/utils'
import { getI18n } from '@/libs/i18n'

const TRIAL_TYPE = 'trial'
const CARD_TYPE = 'card'
const WIRE_TRANSFER_TYPE = 'wire transfer'
const ADMIN_TYPE = '4grit'
const COUPON_TYPE = 'coupon'
const SYSTEM_TYPE1 = 'general'
const SYSTEM_TYPE2 = 'system'
const FREE = 'free'

export const PLAN_NUMBER = {
  everyone: 300,
  Everyone10S: 350,
  Standard10S: 650,
  starter: 401,
  basic: 500,
  standard: 600,
  premium: 700,
  pro: 750
}

export function getNormalizedPlanData (rawData) {
  const data = {
    hasPlan: false,
    isUnpaid: false,
    isCardPlan: false,
    isWirePlan: false,
    byCoupon: false,
    bySystem: false,
    byAdmin: false,
    canChange: false,
    canChargePv: false,
    planName: null,
    planNumber: null,
    startDate: null,
    endDate: null,
    monthlyStartDate: null,
    monthlyEndDate: null,
    pv: null,
    feature: getPlanFeature(null),
    stickyData: null,
    next: null,
    bFree: true,
    inactive: false,
    userId: null
  }

  const today = new Date()

  if (rawData) {
    const currentPlan = rawData.currentPlan

    if (currentPlan) {
      data.hasPlan = true
      data.canChargePv = true
      data.planName = currentPlan.planName
      data.planNumber = currentPlan.planNumber
      data.applyType = currentPlan.applyType
      data.startDate = parse(currentPlan.useStartDate)
      data.endDate = parse(currentPlan.useEndDate)
      data.userId = currentPlan.userId

      if (data.planName === 'FreeOver') {
        data.hasPlan = false
        data.inactive = true
        data.canChargePv = false
      } else if (data.planName === 'Terminate') {
        data.hasPlan = false
        data.canChargePv = false
      }
    }

    let monthlyStartDate = startOfDay(parse(data.startDate))
    let monthlyEndDate = endOfDay(parse(data.endDate))

    if (isWithinRange(startOfDay(today), startOfDay(data.startDate), endOfDay(data.endDate))) {
      while (!isWithinRange(startOfDay(today), monthlyStartDate, monthlyEndDate)) {
        monthlyStartDate = addMonths(monthlyStartDate, 1)
        monthlyEndDate = endOfDay(addDays(addMonths(monthlyStartDate, 1), -1))
      }
    }

    if (monthlyEndDate > endOfDay(data.endDate)) {
      monthlyEndDate = endOfDay(data.endDate)
    }
    data.monthlyStartDate = monthlyStartDate
    data.monthlyEndDate = monthlyEndDate

    if (currentPlan) {
      if (currentPlan.paymentStatus === 'unpaid') {
        data.isUnpaid = true
        data.canChargePv = false
      }

      data.byCoupon = currentPlan.paymentType === COUPON_TYPE
      data.bySystem = currentPlan.paymentType === SYSTEM_TYPE1 || currentPlan.paymentType === SYSTEM_TYPE2 || currentPlan.paymentType === FREE
      data.byAdmin = currentPlan.paymentType === ADMIN_TYPE
      data.isCardPlan = currentPlan.paymentType === CARD_TYPE
      data.isWirePlan = currentPlan.paymentType === WIRE_TRANSFER_TYPE
      data.isTrial = currentPlan.paymentType === TRIAL_TYPE
    }

    if (!data.inactive) {
      if (data.isCardPlan || data.bySystem || data.byCoupon || data.isTrial) {
        data.canChange = true
      }
      if (data.next && (data.next.isWirePlan || data.next.byAdmin || data.applyType === 'admin')) {
        data.canChange = false
      }
    }

    if (currentPlan) {
      data.pv = currentPlan.pvLimit
      data.bFree = currentPlan.bFree
    }
    data.next = getNextPlanData(rawData)
    data.feature = getPlanFeature(data)
    data.stickyData = getPlanStickyData(data)
    data.paidRange = getPaidRange(rawData.paid)
    data.unpaidRange = getPaidRange(rawData.unpaid)
  }

  return data
}

function getPlanFeature (plan) {
  const options = {
    domainCount: 1,
    viewMore: false,
    campaignTargeting: {
      utm: false
    }
  }

  if (plan && !plan.bFree) {
    options.domainCount = -1
    options.viewMore = true
    options.campaignTargeting.utm = true
  }

  return options
}

function getNextPlanData (rawData) {
  let nextPlan = rawData.nextPlan
  let data = null
  if (nextPlan) {
    const today = startOfDay(new Date())
    const nextPlanStart = addMinutes(startOfDay(nextPlan.useStartDate), -1)
    const expireRemains = differenceInDays(nextPlanStart, today)

    data = {
      planName: nextPlan.planName,
      planNumber: nextPlan.planNumber,
      bFree: nextPlan.bFree,
      startDate: parse(nextPlan.useStartDate),
      endDate: parse(nextPlan.useEndDate),
      isCardPlan: nextPlan.paymentType === CARD_TYPE,
      isWirePlan: nextPlan.paymentType === WIRE_TRANSFER_TYPE,
      byCoupon: nextPlan.paymentType === COUPON_TYPE,
      bySystem: nextPlan.paymentType === SYSTEM_TYPE1 || nextPlan.paymentType === SYSTEM_TYPE2 || nextPlan.paymentType === FREE || nextPlan.paymentType === TRIAL_TYPE,
      byAdmin: nextPlan.paymentType === ADMIN_TYPE,
      isUpgrade: nextPlan.isUpgrade,
      isDowngrade: nextPlan.isDowngrade,
      isDowngradeSoon: nextPlan.isDowngrade && expireRemains >= 0 && expireRemains <= 31,
      isSame: nextPlan.isSame
    }
  }
  return data
}

function getPaidRange (data) {
  let result = []
  if (data) {
    for (const plan of data) {
      result.push({
        start: new Date(plan.useStartDate),
        end: new Date(plan.useEndDate)
      })
    }
  }
  return result
}

function getPlanStickyData (planData) {
  let data = null
  const i18n = getI18n()

  if (!planData.hasPlan) {
    if (planData.inactive) {
      data = {
        id: 'inactive',
        type: 'modal',
        title: i18n.t('payment.dialog.inactive.title'),
        dateText: i18n.t('payment.dialog.inactive.dateText'),
        description: i18n.t('payment.dialog.inactive.description', { userId: planData.userId }),
        buttonText: i18n.t('payment.dialog.inactive.buttonText'),
        action: 'endInactive'
      }
      return data
    } else {
      data = {
        id: 'noPlan',
        title: i18n.t('payment.dialog.noPlan.title'),
        description: i18n.t('payment.dialog.noPlan.description'),
        buttonText: i18n.t('payment.dialog.noPlan.buttonText'),
        action: 'contactPlan'
      }
      return data
    }
  }

  if (planData.isUnpaid) {
    data = {
      id: 'unpaid',
      title: i18n.t('payment.dialog.unpaid.title'),
      description: i18n.t('payment.dialog.unpaid.description'),
      buttonText: i18n.t('payment.dialog.unpaid.buttonText'),
      action: 'goBilling'
    }
  } else {
    if (planData.next) {
      if (planData.next.isDowngradeSoon) {
        const changeDate = format(planData.next.startDate, 'YYYY-MM-DD ')

        if (planData.next.isCardPlan || planData.next.bySystem) {
          data = {
            id: 'cardTypeDowngrade',
            title: i18n.t('payment.dialog.cardTypeDowngrade.title', {
              changeDate,
              nextPlanName: planData.next.planName
            }),
            description: i18n.t('payment.dialog.cardTypeDowngrade.description'),
            buttonText: i18n.t('payment.dialog.cardTypeDowngrade.buttonText'),
            action: 'cancelPlanChange'
          }
          if (planData.applyType === 'admin') {
            data.buttonText = i18n.t('payment.dialog.cardTypeDowngrade.adminPlanButtonText')
            data.action = 'contactUs'
          }
        } else if (planData.next.isWirePlan || planData.next.byAdmin) {
          data = {
            id: 'notCardTypeDowngrade',
            title: i18n.t('payment.dialog.notCardTypeDowngrade.title', {
              changeDate,
              nextPlanName: planData.next.planName
            }),
            description: i18n.t('payment.dialog.notCardTypeDowngrade.description'),
            buttonText: i18n.t('payment.dialog.notCardTypeDowngrade.buttonText'),
            action: 'contactUsDowngrade'
          }
        }
      }
    }
  }
  return data
}

/* global IMP */

export function loadPaymentScripts () {
  const files = [
    'https://code.jquery.com/jquery-1.12.4.min.js',
    'https://cdn.iamport.kr/js/iamport.payment-1.1.5.js'
  ]

  return loadScript(files[0]).then(() => {
    return loadScript(files[1])
  })
}

const IAMPORT_ID = 'imp95788244'
let isReady = false

export function requestPayment (params) {
  return new Promise((resolve, reject) => {
    loadPaymentScripts().then(() => {
      if (!isReady) {
        IMP.init(IAMPORT_ID)
      }
      isReady = true
      IMP.request_pay(params, function (result) {
        if (result.success) {
          resolve({
            customerUid: params.customer_uid,
            data: result
          })
        } else {
          const error = new Error('payment failed')
          error.data = {
            customerUid: params.customer_uid,
            data: result
          }
          reject(error)
        }
      })
    }).catch(reject)
  })
}
